"use client";

import { useMemo, type ComponentType, type FC } from "react";
import {
  MonarchSlotContext,
  useMonarchSlotContext,
} from "@redventures/cohesion-utils-react";

import { useAppContext } from "@/context/AppContext";
import { MonarchMetadataProvider } from "@/context/MonarchMetadataContext";
import { extractMonarchMetadata } from "@/helpers/monarch";
import { applyMonarchRuleOverrides } from "@/helpers/monarchInterpolation";

const withMonarchMetadata = <P extends object>(
  WrappedComponent: ComponentType<P>
): FC<P> => {
  const ComponentWithMonarchMetadata: FC<P> = (props) => {
    const slotContext = useMonarchSlotContext();
    const { globalRules } = useAppContext();
    const monarchMetadata = extractMonarchMetadata(slotContext.result, {
      isLoading: slotContext.isLoading,
    });
    const resolvedData = useMemo(
      () =>
        slotContext.data
          ? applyMonarchRuleOverrides(slotContext.data, globalRules)
          : null,
      [globalRules, slotContext.data]
    );
    const resolvedResult = useMemo(
      () =>
        slotContext.result && resolvedData
          ? {
              ...slotContext.result,
              value: {
                ...slotContext.result.value,
                data: resolvedData,
              },
            }
          : slotContext.result,
      [resolvedData, slotContext.result]
    );

    return (
      <MonarchSlotContext.Provider
        value={{
          ...slotContext,
          result: resolvedResult,
          data: resolvedData,
        }}
      >
        <MonarchMetadataProvider monarchMetadata={monarchMetadata}>
          <WrappedComponent {...props} />
        </MonarchMetadataProvider>
      </MonarchSlotContext.Provider>
    );
  };

  ComponentWithMonarchMetadata.displayName = `withMonarchMetadata(${
    WrappedComponent.displayName ?? WrappedComponent.name ?? "Component"
  })`;

  return ComponentWithMonarchMetadata;
};

export default withMonarchMetadata;
